import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { UserPlus, At, PaperPlaneTilt, Robot } from '@phosphor-icons/react';

const steps = [
  {
    icon: UserPlus,
    title: 'Register',
    body: 'Create your account in a few seconds. No credit card needed.',
  },
  {
    icon: At,
    title: 'Get your custom email',
    body: 'We generate a personal address just for your follow-ups.',
  },
  {
    icon: PaperPlaneTilt,
    title: 'CC us on any email',
    body: 'Add your custom address to CC whenever you need a reply.',
  },
  {
    icon: Robot,
    title: 'AI chases it to resolution',
    body: 'We follow up, verify the response, and close the loop for you.',
  },
];

const POSITIONS = [0.125, 0.375, 0.625, 0.875];
const ARRIVE_TIMES = [0.05, 0.32, 0.57, 0.82];

export function HowItWorks() {
  const reduce = useReducedMotion();
  const trackRef = useRef(null);
  const [trackWidth, setTrackWidth] = useState(0);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const update = () => setTrackWidth(el.offsetWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const stops = POSITIONS.map((f) => f * trackWidth);

  return (
    <section id="how-it-works" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
      <h2 className="text-3xl md:text-4xl font-bold text-zinc-100 text-center mb-16">How it works</h2>

      <div ref={trackRef} className="relative">
        <svg className="pointer-events-none absolute left-0 top-8 hidden h-px w-full md:block" preserveAspectRatio="none">
          <line x1="12.5%" y1="0" x2="87.5%" y2="0" stroke="rgb(63 63 70)" strokeWidth="2" />
          <motion.line
            x1="12.5%"
            y1="0"
            x2="87.5%"
            y2="0"
            stroke="rgb(129 140 248)"
            strokeWidth="2"
            initial={reduce ? false : { pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 1.4, ease: 'easeInOut' }}
          />
        </svg>

        {!reduce && trackWidth > 0 && (
          <motion.div
            aria-hidden="true"
            className="pointer-events-none absolute left-0 top-8 z-10 hidden h-5 w-5 -translate-x-1/2 -translate-y-1/2 md:block"
            animate={{
              x: [stops[0], stops[0], stops[1], stops[1], stops[2], stops[2], stops[3], stops[3], stops[3], stops[0]],
              opacity: [0, 1, 1, 1, 1, 1, 1, 1, 0, 0],
            }}
            transition={{
              duration: 7,
              repeat: Infinity,
              ease: 'easeInOut',
              times: [0, 0.05, 0.25, 0.32, 0.5, 0.57, 0.75, 0.82, 0.95, 1],
            }}
          >
            <div className="h-5 w-5 rounded-full bg-indigo-400 shadow-[0_0_14px_rgba(129,140,248,0.9)]" />
          </motion.div>
        )}

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: reduce ? 0 : 0.15 } } }}
          className="relative grid gap-12 md:grid-cols-4"
        >
          {steps.map((step, i) => {
            const arrive = ARRIVE_TIMES[i];
            return (
              <motion.div
                key={step.title}
                variants={{
                  hidden: reduce ? {} : { opacity: 0, y: 24 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
                }}
                className="text-center"
              >
                <motion.div
                  animate={reduce ? undefined : { scale: [1, 1, 1.12, 1, 1] }}
                  transition={
                    reduce
                      ? undefined
                      : {
                          duration: 7,
                          repeat: Infinity,
                          ease: 'easeInOut',
                          times: [0, Math.max(arrive - 0.03, 0), arrive, Math.min(arrive + 0.05, 1), 1],
                        }
                  }
                  className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-indigo-400/30 bg-zinc-900 text-indigo-300 shadow-lg shadow-indigo-950/50"
                >
                  <step.icon weight="bold" className="h-7 w-7" />
                  <span className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-indigo-500 text-xs font-bold text-white">
                    {i + 1}
                  </span>
                </motion.div>
                <h3 className="mt-4 text-xl font-semibold text-zinc-100">{step.title}</h3>
                <p className="mt-2 text-sm text-zinc-400">{step.body}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
